import { useEffect, useMemo, useState } from "react";
import type { BarConfig } from "../../../components/Charts";
import chartDataFetcher, { type P4RawDataPoint } from "../../../services/api/ChartDataFetch";

export interface P4FilterValues {
  titulacions: string[];
}

export interface P4ChartPoint extends Record<string, string | number> {
  nom_titulacio: string;
  index_desajust: number;
}

const BARS: BarConfig[] = [
  { key: "index_desajust", label: "Índex de desajust", color: "#3498DB" },
];

function transformData(raw: P4RawDataPoint[], titulacions: string[]): P4ChartPoint[] {
  const filtered = titulacions.length > 0
    ? raw.filter((d) => titulacions.includes(d.nom_titulacio))
    : raw;

  return filtered
    .map((d) => ({
      nom_titulacio: d.nom_titulacio,
      index_desajust: d.index_desajust,
    }))
    .sort((a, b) => b.index_desajust - a.index_desajust);
}

export function useP4Data(filters: P4FilterValues) {
  const [rawData, setRawData] = useState<P4RawDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    chartDataFetcher
      .getP4Data()
      .then((data) => { if (!cancelled) setRawData(data); })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Error carregant dades");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const availableTitulacions = useMemo(
    () => Array.from(new Set(rawData.map((d) => d.nom_titulacio))).sort(),
    [rawData]
  );

  const chartData = useMemo(
    () => transformData(rawData, filters.titulacions),
    [rawData, filters.titulacions]
  );

  return { chartData, bars: BARS, availableTitulacions, loading, error };
}
